// AdMob rewarded ads ("Watch ad, get coins").
// Works ONLY inside the Android (Capacitor) app that has the @capacitor-community/admob plugin installed natively.
// On the website there is no AdMob, so `adsAvailable()` is false and the UI shows "Watch ads in the Animora app".
// Set your real ids in the env (NEXT_PUBLIC_ADMOB_REWARDED_ID) - until then Google's TEST rewarded id is used.
import { isNativeApp } from '@/lib/native-app'
import { ADMOB_FORCE_TEST } from '@/lib/rewards-policy'

const TEST_REWARDED_ID = 'ca-app-pub-3940256099942544/5224354917' // Google's official test rewarded ad unit
// Animora's real AdMob Rewarded ad unit ("Watch ad, get coins"). App ID (goes in AndroidManifest.xml, NOT here):
// ca-app-pub-8109715582603317~3292503977
const LIVE_REWARDED_ID = 'ca-app-pub-8109715582603317/3384089039'
// NEXT_PUBLIC_ADMOB_REWARDED_ID can still override the id. Set NEXT_PUBLIC_ADMOB_TEST=1 to force Google's test ads
// (use this on your own phone while developing - never click your own live ads).
const IS_TESTING = ADMOB_FORCE_TEST || process.env.NEXT_PUBLIC_ADMOB_TEST === '1'
const TEST_REWARDED_INTERSTITIAL_ID = 'ca-app-pub-3940256099942544/5354046379' // Google's official test rewarded interstitial
const LIVE_REWARDED_INTERSTITIAL_UNIT = 'ca-app-pub-8109715582603317/5083117855' // Animora "Rewarded interstitial" unit
const REWARDED_INTERSTITIAL_ID = IS_TESTING ? TEST_REWARDED_INTERSTITIAL_ID : (process.env.NEXT_PUBLIC_ADMOB_REWARDED_INTERSTITIAL_ID || LIVE_REWARDED_INTERSTITIAL_UNIT)
export type AdFormat = 'rewarded' | 'interstitial'
const REWARDED_ID = IS_TESTING ? TEST_REWARDED_ID : (process.env.NEXT_PUBLIC_ADMOB_REWARDED_ID || LIVE_REWARDED_ID)

// The site runs inside the Capacitor WebView (loaded from the live URL), so - like Share / Push in this project -
// the native plugin is reached through window.Capacitor, no npm import in the web bundle.
// Old app builds without the plugin simply get `null` here, and the UI shows "Watch ads in the Animora app".
const plugin = (): any => {
  if (typeof window === 'undefined') return null
  const cap = (window as any).Capacitor
  try {
    if (cap?.Plugins?.AdMob) return cap.Plugins.AdMob
    if (typeof cap?.isPluginAvailable === 'function' && cap.isPluginAvailable('AdMob') && typeof cap.registerPlugin === 'function') return cap.registerPlugin('AdMob')
  } catch { /* plugin not installed in this build */ }
  return null
}

export const adsAvailable = (): boolean => isNativeApp() && !!plugin()

// Tracks any full-screen ad (rewarded / rewarded interstitial / loss interstitial / app open). When such an ad closes, the app
// returns to the foreground and fires `visibilitychange` - which used to trigger an App Open ad right on top of it (annoying and
// against AdMob policy). App Open now stays quiet while a full-screen ad is open and for a short time after it closed.
let fullscreenActive = 0
let fullscreenClosedAt = 0
const FULLSCREEN_QUIET_MS = 20_000
export const fullscreenAdBusy = (): boolean => fullscreenActive > 0 || Date.now() - fullscreenClosedAt < FULLSCREEN_QUIET_MS
const fullscreenStart = () => { fullscreenActive++ }
const fullscreenEnd = () => { fullscreenActive = Math.max(0, fullscreenActive - 1); fullscreenClosedAt = Date.now() }

// ---------------------------------------------------------------------------
// ADS CONSENT (Google UMP). In the EEA / UK (and some other regions) Google requires a consent form before ads load.
// The form text itself is made in AdMob > Privacy & messaging; this only asks the plugin to show it when Google says it is REQUIRED.
// Never blocks: if the plugin has no consent API (old build), the form is not set up, or it takes too long, ads continue as before.
// To TEST the form on a normal phone: set NEXT_PUBLIC_ADMOB_CONSENT_DEBUG=1 (pretends to be in the EEA).
// ---------------------------------------------------------------------------
let consentDone: Promise<void> | null = null
function ensureConsent(): Promise<void> {
  if (consentDone) return consentDone
  consentDone = (async () => {
    const AdMob = plugin()
    if (!AdMob || typeof AdMob.requestConsentInfo !== 'function') return
    try {
      const debug = process.env.NEXT_PUBLIC_ADMOB_CONSENT_DEBUG === '1'
      const info = await withTimeout(Promise.resolve(AdMob.requestConsentInfo(debug ? { debugGeography: 1 } : {})), 8000, 'Consent info timeout')
      const required = info?.status === 'REQUIRED' || info?.status === 1
      if (info?.isConsentFormAvailable && required && typeof AdMob.showConsentForm === 'function') {
        await AdMob.showConsentForm() // resolves when the person has answered; ads then respect their choice
      }
    } catch (e: any) { console.warn('[ads] consent skipped', e?.message || e) }
  })()
  return consentDone
}

// ---------------------------------------------------------------------------
// AUDIENCE. Animora allows 13-17 year-olds (the "teen" age band). For them AdMob must be told they are under the age of
// consent (no personalised ads) and the ad content rating is capped at Teen. The band comes ONLY from the server
// (/api/account/age); app/page.tsx calls setAdAudience() as soon as it is known and holds back App Open / banner until then.
// ---------------------------------------------------------------------------
let adAudience: { minor: boolean } = { minor: false }
export function setAdAudience(a: { minor: boolean }) { adAudience = { minor: !!a.minor } }

let inited = false
let initPromise: Promise<void> | null = null
async function init() {
  if (inited) return
  // Shared promise: warm-up and a tap can call init() at the same time without initialising twice.
  if (!initPromise) {
    initPromise = (async () => {
      await ensureConsent()
      await plugin().initialize({
        initializeForTesting: IS_TESTING,
        ...(adAudience.minor ? { tagForUnderAgeOfConsent: true, maxAdContentRating: 'Teen' } : {}),
      })
      inited = true
    })().catch((e) => { initPromise = null; throw e })
  }
  await initPromise
}

// ---------------------------------------------------------------------------
// Preloading. Loading a rewarded ad takes seconds, so it is started BEFORE the user taps
// (when the Wallet / Boost card appears, and again right after every ad), instead of after the tap.
// Tapping "Watch ad" then shows the already-loaded ad almost instantly.
// ---------------------------------------------------------------------------
const PREPARE_TIMEOUT_MS = 20_000 // never spin forever: give up and let the user retry
const AD_STALE_MS = 45 * 60 * 1000 // Google expires a loaded ad after ~1 hour; reload before that
const ready: Record<AdFormat, { p: Promise<void>; at: number; uid: string } | null> = { rewarded: null, interstitial: null }

// SERVER-SIDE VERIFICATION: the signed-in uid travels with every rewarded ad (AdMob `ssv.userId`), so Google's callback to
// /api/rewards/ssv says WHO really watched. It is taken from the Firebase ID token (no extra plumbing needed in the UI).
let ssvUid = ''
export function setAdUserId(uid: string | null | undefined) { ssvUid = String(uid || '') }
function uidFromIdToken(idToken: string): string {
  try {
    const part = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = JSON.parse(decodeURIComponent(escape(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '=')))))
    return String(json.user_id || json.sub || '')
  } catch { return '' }
}

const FORMAT = {
  rewarded: { id: () => REWARDED_ID, prepare: 'prepareRewardVideoAd', show: 'showRewardVideoAd', failed: 'onRewardedVideoAdFailedToLoad', reward: 'onRewardedVideoAdReward' },
  interstitial: { id: () => REWARDED_INTERSTITIAL_ID, prepare: 'prepareRewardInterstitialAd', show: 'showRewardInterstitialAd', failed: 'onRewardedInterstitialAdFailedToLoad', reward: 'onRewardedInterstitialAdReward' },
} as const

// Native events fired when the full-screen ad is closed / could not be shown (same plugin names as FORMAT above).
const CLOSE_EVENTS = {
  rewarded: { dismissed: 'onRewardedVideoAdDismissed', failedToShow: 'onRewardedVideoAdFailedToShow' },
  interstitial: { dismissed: 'onRewardedInterstitialAdDismissed', failedToShow: 'onRewardedInterstitialAdFailedToShow' },
} as const

// BUG FIX ("Ad is not ready" in the Wallet after the game's double-win ad):
// the plugin keeps loaded ads in a map keyed by the ad UNIT id, and REMOVES that entry when the shown ad is dismissed.
// We used to start loading the next ad 150 ms after the reward - while the first ad was still on screen. The new ad
// registered itself under the same unit id, then the person closed the first ad and the plugin deleted the NEW one.
// JS still believed an ad was ready, so the next tap (Wallet gift box) failed. Now nothing is loaded while a
// full-screen ad is open: prepare() waits on this gate, which opens a moment after the ad is really closed.
let adClosedGate: Promise<void> | null = null
const waitAdClosed = async () => { if (adClosedGate) await adClosedGate }
const isNotPreparedError = (e: any) => /not prepared|failed to be prepared|no reward|not loaded|not ready/i.test(String(e?.message || e || ''))

function prepare(fmt: AdFormat = 'rewarded'): Promise<void> {
  const AdMob = plugin()
  const f = FORMAT[fmt]
  const uid = ssvUid
  const p = (async () => {
    await waitAdClosed() // never load while another ad is on screen (see note above)
    await init()
    if (typeof AdMob[f.prepare] !== 'function') throw new Error('This ad type is not supported in your app version.')
    let failHandle: any
    let timer: ReturnType<typeof setTimeout> | undefined
    const failP = new Promise<never>((_, reject) => {
      // Fail fast when Google says "no ad" instead of waiting for the timeout.
      Promise.resolve(AdMob.addListener(f.failed, () => reject(new Error('No ad available right now. Try again in a bit.'))))
        .then((h: any) => { failHandle = h }).catch(() => { /* older plugin: timeout still protects us */ })
    })
    const timeoutP = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Ad is taking too long to load. Please try again.')), PREPARE_TIMEOUT_MS)
    })
    failP.catch(() => {}); timeoutP.catch(() => {})
    try {
      await Promise.race([AdMob[f.prepare]({ adId: f.id(), isTesting: IS_TESTING, ...(uid ? { ssv: { userId: uid } } : {}) }), failP, timeoutP])
    } finally {
      if (timer) clearTimeout(timer)
      try { await failHandle?.remove?.() } catch { /* ignore */ }
    }
  })()
  const entry = { p, at: Date.now(), uid }
  ready[fmt] = entry
  p.catch((e) => { if (ready[fmt] === entry) ready[fmt] = null; console.warn('[ads] load failed', fmt, e?.message || e) }) // a failed load is retried on the next warm-up / tap
  return p
}

/** Starts loading the next rewarded ad in the background (safe to call any time, does nothing on the website).
 *  If the load fails (no fill / slow network) it quietly retries a few times, so a ready ad is usually waiting when the user taps. */
const warmRetries: Record<AdFormat, number> = { rewarded: 0, interstitial: 0 }
export function warmUpAd(fmt: AdFormat = 'rewarded'): void {
  if (!adsAvailable()) return
  const r = ready[fmt]
  if (r && Date.now() - r.at < AD_STALE_MS && r.uid === ssvUid) return // already loaded or loading
  void prepare(fmt).then(() => { warmRetries[fmt] = 0 }).catch(() => {
    if (warmRetries[fmt] >= 4) return // the tap will retry and show the error
    warmRetries[fmt]++
    setTimeout(() => warmUpAd(fmt), Math.min(15_000, 2_000 * warmRetries[fmt]))
  })
}

/** Call once when the signed-in user is known (app start): loads the rewarded ad (and the boost one) BEFORE anyone opens the wallet,
 *  so the first tap on a gift box shows the ad at once. Also reloads when the app comes back to the foreground. */
let preloadBound = false
export function startAdPreload(uid: string | null | undefined): void {
  if (!uid || !adsAvailable()) return
  setAdUserId(uid)
  warmUpAd('rewarded')
  setTimeout(() => warmUpAd('interstitial'), 4_000) // second unit a little later so it never competes with the first
  if (preloadBound || typeof document === 'undefined') return
  preloadBound = true
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && ssvUid) { warmUpAd('rewarded') } })
}

/** Resolves when an ad is loaded (starting the load if needed). */
export function ensureAdReady(fmt: AdFormat = 'rewarded'): Promise<void> {
  if (!plugin() || !isNativeApp()) return Promise.reject(new Error('Ads are available in the Animora Android app.'))
  const r = ready[fmt]
  if (r && Date.now() - r.at < AD_STALE_MS && r.uid === ssvUid) return r.p
  return prepare(fmt)
}

/** Shows one rewarded ad (video or rewarded interstitial). Resolves true only if the user earned the reward. */
export async function showRewardedAd(fmt: AdFormat = 'rewarded'): Promise<boolean> {
  if (!plugin() || !isNativeApp()) throw new Error('Ads are available in the Animora Android app.')
  try {
    return await showRewardedOnce(fmt)
  } catch (e: any) {
    // The native side lost the loaded ad (it says "not prepared"): load a fresh one and try once more.
    if (!isNotPreparedError(e)) throw e
    ready[fmt] = null
    try { return await showRewardedOnce(fmt) }
    catch (e2: any) {
      if (isNotPreparedError(e2)) throw new Error('Ad is not ready right now. Try again in a bit.')
      throw e2
    }
  }
}

async function showRewardedOnce(fmt: AdFormat): Promise<boolean> {
  const AdMob = plugin()
  const f = FORMAT[fmt]
  const ev = CLOSE_EVENTS[fmt]
  await waitAdClosed()
  const cur = ready[fmt]
  const entry = cur && Date.now() - cur.at < AD_STALE_MS && cur.uid === ssvUid ? cur : (prepare(fmt), ready[fmt]!)
  try {
    await entry.p
  } catch (e) {
    if (ready[fmt] === entry) ready[fmt] = null
    throw e
  }
  ready[fmt] = null // a loaded ad can be shown only once

  let rewarded = false
  let closeNow: () => void = () => {}
  const closed = new Promise<void>((r) => { closeNow = r })
  const safety = setTimeout(() => closeNow(), 3 * 60 * 1000) // never keep the gate shut forever
  const handles: any[] = []
  const on = async (name: string, fn: () => void) => {
    try { const h = await AdMob.addListener(name, fn); if (h) handles.push(h) } catch { /* older plugin */ }
  }
  await on(f.reward, () => { rewarded = true })
  await on(ev.dismissed, () => closeNow())
  await on(ev.failedToShow, () => closeNow())

  // Gate: stays shut until the ad is really closed (+ a short pause), so the next ad is never loaded underneath it.
  const gate: Promise<void> = closed.then(() => new Promise<void>((r) => setTimeout(r, 600))).then(() => { if (adClosedGate === gate) adClosedGate = null })
  adClosedGate = gate

  fullscreenStart()
  void closed.then(async () => {
    clearTimeout(safety)
    fullscreenEnd()
    for (const h of handles) { try { await h?.remove?.() } catch { /* ignore */ } }
    void gate.then(() => warmUpAd(fmt)) // load the next one only now, while the person is back on the wallet
  })

  try {
    // show() resolves with the reward item (and never at all if the ad is closed early): finish on the reward OR on close.
    const showP = Promise.resolve(AdMob[f.show]()).then((item: any) => { if (item && (typeof item.amount === 'number' || item.type)) rewarded = true })
    showP.catch(() => {})
    await Promise.race([showP, closed])
    if (!rewarded) await new Promise((r) => setTimeout(r, 1200)) // the reward event can land just after the close event
  } catch (e) {
    closeNow() // nothing is on screen: open the gate again
    throw e
  }
  return rewarded
}

export type AdStatus = {
  remaining: number; cap: number; cooldownLeftMs: number; coinsPerAd: number; coinsMax?: number
  // Gift-box row (new): fixed coins per box, a row of boxes that resets every hour.
  nextReward?: number; rewards?: number[]; rowSize?: number; rowOpened?: number; rowFull?: boolean; rowResetMs?: number; openedIndex?: number
}

const call = async (idToken: string, body: Record<string, unknown>) => {
  const res = await fetch('/api/rewards', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.ok) {
    const MSG: Record<string, string> = {
      'too-fast': 'Please watch the full ad.',
      'bad-ticket': 'This ad session ended. Please tap Watch ad again.',
      'ticket-expired': 'This ad session expired. Please tap Watch ad again.',
      'no-account': 'Please sign in again.',
      'unauthenticated': 'Please sign in again.',
    }
    const msg = MSG[data.error] || (res.status === 429 ? 'Too many tries. Wait a moment and try again.' : 'Could not reach the server. Try again.')
    throw Object.assign(new Error(msg), { code: data.error, status: res.status })
  }
  return data
}

export const fetchAdStatus = async (idToken: string): Promise<AdStatus> => { setAdUserId(uidFromIdToken(idToken)); return call(idToken, { action: 'ad_status' }) }

/** Full flow: ticket -> ad -> claim. Returns coins granted (0 if the ad was skipped / limit reached) + fresh status. */
export async function watchAdForCoins(idToken: string, format: AdFormat = 'rewarded'): Promise<{ granted: number; status: AdStatus; reason?: 'capped' | 'cooldown' | 'skipped' | 'rowWait' }> {
  // Ticket request and ad loading run TOGETHER (before, the ad only started loading after the ticket came back).
  // If the load fails here, showRewardedAd() below retries it and reports the real error.
  setAdUserId(uidFromIdToken(idToken))
  const [start] = await Promise.all([call(idToken, { action: 'ad_start' }), ensureAdReady(format).catch(() => undefined)])
  if (start.capped) return { granted: 0, status: start, reason: 'capped' }
  if (start.cooldown) return { granted: 0, status: start, reason: 'cooldown' }
  if (start.rowWait) return { granted: 0, status: start, reason: 'rowWait' }
  // Rewarded interstitial not available (old app build / no fill)? Fall back to the normal rewarded video so the tap still pays.
  let watched: boolean
  try { watched = await showRewardedAd(format) }
  catch (e) { if (format === 'interstitial') watched = await showRewardedAd('rewarded'); else throw e }
  if (!watched) return { granted: 0, status: start, reason: 'skipped' }
  // Google's SSV callback usually lands within a second or two of the ad closing: retry while the server says 'ssv-pending'.
  // Retries: 'ssv-pending' = Google's callback not here yet; 'too-fast' = ad paid out sooner than the server's minimum (just wait, the
  // ticket stays valid); no status at all = network blip (the ticket is still unused, so the claim is safe to repeat).
  let done: any
  for (let attempt = 0; ; attempt++) {
    try { done = await call(idToken, { action: 'ad_claim', ticket: start.ticket }); break }
    catch (e: any) {
      const retryable = e?.code === 'ssv-pending' || e?.code === 'too-fast' || (e?.status == null) || e?.status >= 500
      if (!retryable || attempt >= 7) throw e
      await new Promise((r) => setTimeout(r, e?.code === 'too-fast' ? 2000 : 1500))
    }
  }
  return { granted: Number(done.granted || 0), status: done, reason: done.capped ? 'capped' : done.rowWait ? 'rowWait' : undefined }
}

// ---------------------------------------------------------------------------
// TOP BANNER (adaptive). Shown on every signed-in screen (Home, Chats, Discover, Profile, Settings, Games, Ludo, Chess ...)
// EXCEPT the Reels section. The banner is a NATIVE view (AdMob has no web version), so - like the rewarded ad -
// it only exists inside the Android app that has the @capacitor-community/admob plugin.
//
// Layout: the plugin pushes the WebView down by the banner height. We MEASURE how far the page really moved
// (window.innerHeight before / after) and publish the part of the top still covered by the banner as
// --ad-covered on <html>. globals.css then uses it (html.ad-banner-on) to keep every header clear of the banner,
// whatever the native side did. If no banner loads, nothing changes and the layout stays exactly as before.
// ---------------------------------------------------------------------------
const TEST_BANNER_ID = 'ca-app-pub-3940256099942544/9214589741' // Google's official test adaptive banner
const LIVE_BANNER_ID = 'ca-app-pub-8109715582603317/3373172677' // Animora "Banner" ad unit
const BANNER_ID = IS_TESTING ? TEST_BANNER_ID : (process.env.NEXT_PUBLIC_ADMOB_BANNER_ID || LIVE_BANNER_ID)

let bannerShowing = false
let bannerOp = 0 // bumped on every show / hide so late async results of an old call are ignored
let bannerBeforeH = 0 // window.innerHeight right before the banner was added
let bannerSizeHandle: any = null
let bannerSettleTimer: ReturnType<typeof setTimeout> | undefined

const htmlEl = () => document.documentElement

// Gap (px) kept between the very top of the screen and the banner. Keep it small: the old value (full status-bar min 40px)
// pushed the ad down onto the Animora wordmark. ~24px is a normal status bar; set 0 to put the ad flush with the top.
const BANNER_TOP_MARGIN_MAX = 0
// Extra breathing room (px) between the bottom of the ad and the Animora wordmark.
// AdMob policy: a banner must not sit right next to buttons / navigation (accidental clicks). This is the empty strip (px) that
// is kept between the bottom of the ad and the app's own header, so the first thing under the ad is NOT a tappable element.
const BANNER_LOGO_CLEARANCE = 24

/** Height (px) the page keeps free for the phone's status bar right now (0 in a normal browser tab). */
function statusGapPx(): number {
  try {
    const probe = document.createElement('div')
    probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:0;padding-top:var(--safe-top,0px)'
    htmlEl().appendChild(probe)
    const gap = parseFloat(getComputedStyle(probe).paddingTop) || 0
    probe.remove()
    return Math.max(0, Math.round(gap))
  } catch { return 0 }
}

function clearBannerLayout() {
  if (bannerSettleTimer) { clearTimeout(bannerSettleTimer); bannerSettleTimer = undefined }
  if (typeof document === 'undefined') return
  htmlEl().classList.remove('ad-banner-on')
  htmlEl().style.removeProperty('--ad-covered')
}

function settleBannerLayout(bannerH: number, gap: number, op: number) {
  if (bannerSettleTimer) clearTimeout(bannerSettleTimer)
  // Wait a moment so the WebView has finished resizing before we measure it.
  bannerSettleTimer = setTimeout(() => {
    if (op !== bannerOp || !bannerShowing) return
    const shrink = Math.max(0, bannerBeforeH - window.innerHeight)
    // Part of the top of the page that is still under the banner (status-bar gap + banner - how far the page moved down).
    const covered = Math.max(0, Math.round(bannerH + gap - shrink + BANNER_LOGO_CLEARANCE))
    htmlEl().style.setProperty('--ad-covered', `${covered}px`)
    htmlEl().classList.add('ad-banner-on')
  }, 450)
}

/** Shows the top banner (safe to call repeatedly; does nothing on the website / in old app builds). */
export async function showTopBanner(): Promise<void> {
  const AdMob = plugin()
  if (!AdMob || !isNativeApp() || bannerShowing) return
  const op = ++bannerOp
  bannerShowing = true
  try {
    await init()
    if (op !== bannerOp) return
    const statusInset = statusGapPx() // phone status bar: the native banner is placed BELOW it, so the page must clear it too
    const gap = Math.min(statusInset, BANNER_TOP_MARGIN_MAX)
    bannerBeforeH = window.innerHeight
    try { await bannerSizeHandle?.remove?.() } catch { /* ignore */ }
    bannerSizeHandle = await AdMob.addListener('bannerAdSizeChanged', (size: any) => {
      if (op !== bannerOp || !bannerShowing) return
      let h = Number(size?.height) || 0
      if (h <= 0) { clearBannerLayout(); return }
      const dpr = window.devicePixelRatio || 1
      if (dpr > 1 && h > 100) h = h / dpr // some plugin versions report device pixels
      settleBannerLayout(h, gap + statusInset, op) // banner bottom = status bar + margin + banner height
    })
    if (op !== bannerOp) return
    const show = (adSize: string) => AdMob.showBanner({
      adId: BANNER_ID,
      adSize,
      position: 'TOP_CENTER',
      margin: gap, // sits just below the status bar instead of under the clock / battery icons
      isTesting: IS_TESTING,
    })
    try { await show('ADAPTIVE_BANNER') }
    catch (e1: any) {
      // Some plugin / device combos reject the adaptive size: fall back to the standard 320x50 banner so an ad still shows.
      console.warn('[ads] adaptive banner failed, trying BANNER', e1?.message || e1)
      if (op !== bannerOp) return
      await show('BANNER')
    }
  } catch (e: any) {
    // Banner could not start: leave the layout untouched, and try once more in a minute (fill can be slow on new ad units).
    console.warn('[ads] banner failed', e?.message || e)
    if (op === bannerOp) {
      bannerShowing = false; clearBannerLayout()
      setTimeout(() => { if (op === bannerOp && !bannerShowing) void showTopBanner() }, 60_000)
    }
  }
}

/** Removes the top banner and gives the page its normal layout back (used on the Reels section and on sign-out). */
export async function hideTopBanner(): Promise<void> {
  bannerOp++
  const wasShowing = bannerShowing
  bannerShowing = false
  clearBannerLayout()
  const AdMob = plugin()
  if (!AdMob || !wasShowing) return
  try { await bannerSizeHandle?.remove?.() } catch { /* ignore */ }
  bannerSizeHandle = null
  try { await AdMob.removeBanner() } catch { /* nothing to remove */ }
}

// ---------------------------------------------------------------------------
// APP OPEN AD (no coins). Shown when the app is opened / comes back to the foreground (native only, needs a
// @capacitor-community/admob build that has loadAppOpen / showAppOpen - old builds simply skip it).
// Flow: server says "eligible" (cooldown + daily cap) -> ad is loaded and shown -> when it is closed the server records it
// (`app_open_claim`, 0 coins). The app never decides the amount.
// ---------------------------------------------------------------------------
const TEST_APP_OPEN_ID = 'ca-app-pub-3940256099942544/9257395921' // Google's official test App Open unit (Android)
const LIVE_APP_OPEN_ID = 'ca-app-pub-8109715582603317/5727873279' // Animora "App open ad" unit
const APP_OPEN_ID = IS_TESTING ? TEST_APP_OPEN_ID : (process.env.NEXT_PUBLIC_ADMOB_APP_OPEN_ID || LIVE_APP_OPEN_ID)
// Saved here for later use (Rewarded interstitial unit, not wired to any screen yet).
export const LIVE_REWARDED_INTERSTITIAL_ID = 'ca-app-pub-8109715582603317/5083117855'

export const appOpenAvailable = (): boolean => adsAvailable() && typeof plugin()?.loadAppOpen === 'function' && typeof plugin()?.showAppOpen === 'function'

export type AppOpenResult = { granted: number; reason?: 'unavailable' | 'busy' | 'cooldown' | 'capped' | 'no-ad' | 'failed'; cooldownLeftMs?: number }
let appOpenBusy = false

const withTimeout = <T,>(p: Promise<T>, ms: number, msg: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(msg)), ms)
    p.then((v) => { clearTimeout(t); resolve(v) }, (e) => { clearTimeout(t); reject(e) })
  })

/** Full flow: eligibility -> load -> show -> (closed) -> claim. Never throws; returns coins granted (0 = nothing to show). */
export async function showAppOpenAdForCoins(idToken: string): Promise<AppOpenResult> {
  if (!appOpenAvailable()) return { granted: 0, reason: 'unavailable' }
  if (appOpenBusy || fullscreenAdBusy()) return { granted: 0, reason: 'busy' }
  appOpenBusy = true
  const AdMob = plugin()
  const handles: any[] = []
  try {
    await init()
    const start = await call(idToken, { action: 'app_open_start' })
    if (start.capped) return { granted: 0, reason: 'capped' }
    if (start.cooldown) return { granted: 0, reason: 'cooldown', cooldownLeftMs: Number(start.cooldownLeftMs || 0) }

    let failedToShow = false
    let closedResolve: () => void = () => {}
    const closed = new Promise<void>((r) => { closedResolve = r })
    const on = async (ev: string, fn: () => void) => {
      try { const h = await AdMob.addListener(ev, fn); if (h) handles.push(h) } catch { /* older plugin */ }
    }
    await on('appOpenAdClosed', () => closedResolve())
    await on('appOpenAdFailedToShow', () => { failedToShow = true; closedResolve() })

    try {
      await withTimeout(Promise.resolve(AdMob.loadAppOpen({ adId: APP_OPEN_ID, isTesting: IS_TESTING })), 15_000, 'App open ad load timeout')
    } catch { return { granted: 0, reason: 'no-ad' } }
    try {
      const loaded = typeof AdMob.isAppOpenLoaded === 'function' ? (await AdMob.isAppOpenLoaded())?.value !== false : true
      if (!loaded) return { granted: 0, reason: 'no-ad' }
    } catch { /* assume loaded */ }

    fullscreenStart()
    try {
      try { await AdMob.showAppOpen() } catch { return { granted: 0, reason: 'failed' } }
      if (failedToShow) return { granted: 0, reason: 'failed' }
      // Wait until the user closes the ad (safety timeout so we never hang).
      await Promise.race([closed, new Promise<void>((r) => setTimeout(r, 3 * 60 * 1000))])
    } finally { fullscreenEnd() }
    if (failedToShow) return { granted: 0, reason: 'failed' }

    const done = await call(idToken, { action: 'app_open_claim', ticket: start.ticket })
    return { granted: Number(done.granted || 0), reason: done.capped ? 'capped' : done.cooldown ? 'cooldown' : undefined }
  } catch {
    return { granted: 0, reason: 'failed' }
  } finally {
    for (const h of handles) { try { await h?.remove?.() } catch { /* ignore */ } }
    appOpenBusy = false
  }
}

// ---------------------------------------------------------------------------
// FULL-SCREEN INTERSTITIAL (no coins). Shown to the player who LOSES a Ludo / Chess game, at the natural break
// when they leave the result card ("Back to lobby" / "Play again"). Native app only; on the website it is skipped.
// TEST MODE: IS_TESTING is forced on by ADMOB_FORCE_TEST (lib/rewards-policy.ts), so Google's test interstitial is used.
// When you go live: create an "Interstitial" unit in AdMob, put its id in NEXT_PUBLIC_ADMOB_INTERSTITIAL_ID (or in
// LIVE_INTERSTITIAL_ID below) and set ADMOB_FORCE_TEST = false. Until a live id exists the ad is simply skipped.
// ---------------------------------------------------------------------------
const TEST_INTERSTITIAL_ID = 'ca-app-pub-3940256099942544/1033173712' // Google's official test interstitial
const LIVE_INTERSTITIAL_ID = '' // <- paste your Interstitial ad unit id here when going live
const INTERSTITIAL_ID = IS_TESTING ? TEST_INTERSTITIAL_ID : (process.env.NEXT_PUBLIC_ADMOB_INTERSTITIAL_ID || LIVE_INTERSTITIAL_ID)
const LOSS_AD_COOLDOWN_MS = 5 * 60_000 // never show two loss ads closer than this (keeps players happy, keeps AdMob happy)
const LOSS_AD_EVERY = 4 // only every 4th loss shows an ad (frequency capping: calmer for players, safer for AdMob)
let lossCount = 0

let interstitialLoad: { p: Promise<void>; at: number } | null = null
let lastInterstitialAt = 0
let interstitialBusy = false

/** Starts loading the loss interstitial in the background (call when a game starts, so it is ready when the game ends). */
export function warmUpInterstitial(): void {
  const AdMob = plugin()
  if (!AdMob || !isNativeApp() || !INTERSTITIAL_ID) return
  if (interstitialLoad && Date.now() - interstitialLoad.at < AD_STALE_MS) return
  const p = (async () => {
    await init()
    if (typeof AdMob.prepareInterstitial !== 'function') throw new Error('Interstitial not supported in this app version.')
    await withTimeout(Promise.resolve(AdMob.prepareInterstitial({ adId: INTERSTITIAL_ID, isTesting: IS_TESTING })), PREPARE_TIMEOUT_MS, 'Interstitial load timeout')
  })()
  const entry = { p, at: Date.now() }
  interstitialLoad = entry
  p.catch((e) => { if (interstitialLoad === entry) interstitialLoad = null; console.warn('[ads] interstitial load failed', e?.message || e) })
}

/**
 * Shows the full-screen loss ad if one is ready. Never throws and never blocks the player for long:
 * resolves when the ad is dismissed, when it fails, or (safety) shortly after it started showing.
 */
export async function showLossInterstitial(): Promise<boolean> {
  const AdMob = plugin()
  if (!AdMob || !isNativeApp() || !INTERSTITIAL_ID || interstitialBusy) return false
  if (Date.now() - lastInterstitialAt < LOSS_AD_COOLDOWN_MS) return false
  lossCount++
  if (lossCount % LOSS_AD_EVERY !== 0) return false
  interstitialBusy = true
  const handles: any[] = []
  try {
    if (!interstitialLoad) warmUpInterstitial()
    const cur = interstitialLoad
    if (!cur) return false
    try { await withTimeout(cur.p, 4000, 'Interstitial not ready') } catch { return false } // not loaded in time: skip, never make the player wait
    interstitialLoad = null // a loaded ad can be shown only once
    let done: () => void = () => {}
    const closed = new Promise<void>((r) => { done = r })
    // The plugin renamed its events between versions: listen to both spellings.
    for (const ev of ['interstitialAdDismissed', 'onInterstitialAdDismissed', 'interstitialAdFailedToShow', 'onInterstitialAdFailedToShow']) {
      try { const h = await AdMob.addListener(ev, () => done()); if (h) handles.push(h) } catch { /* older plugin */ }
    }
    lastInterstitialAt = Date.now()
    // showInterstitial() resolves once the ad is on screen; the ad then sits above the app until the player closes it.
    fullscreenStart()
    try { await Promise.race([Promise.resolve(AdMob.showInterstitial()).then(() => new Promise<void>((r) => setTimeout(r, 1200))), closed]) }
    finally { fullscreenEnd() }
    return true
  } catch (e: any) {
    console.warn('[ads] interstitial failed', e?.message || e)
    return false
  } finally {
    for (const h of handles) { try { await h?.remove?.() } catch { /* ignore */ } }
    interstitialBusy = false
    setTimeout(() => warmUpInterstitial(), 1500) // load the next one
  }
}


/**
 * Generic "ticket -> rewarded ad -> claim" used by the DOUBLE-YOUR-WIN box. The caller supplies the two server calls
 * (the game routes own the ticket + payout). Returns the coins granted (0 if the ad was skipped / not available).
 */
export async function runDoubleAd(start: () => Promise<any>, claim: (ticket: string) => Promise<any>): Promise<{ granted: number; reason?: 'skipped' | 'capped' | 'unavailable' }> {
  if (!adsAvailable()) return { granted: 0, reason: 'unavailable' }
  const [st] = await Promise.all([start(), ensureAdReady('rewarded').catch(() => undefined)])
  if (st?.capped) return { granted: 0, reason: 'capped' }
  if (!st?.ticket) return { granted: 0, reason: 'unavailable' }
  const watched = await showRewardedAd('rewarded')
  if (!watched) return { granted: 0, reason: 'skipped' }
  for (let attempt = 0; ; attempt++) {
    try { const done = await claim(st.ticket); return { granted: Number(done?.granted || 0) } }
    catch (e: any) {
      const retryable = e?.message === 'ssv-pending' || e?.message === 'too-fast' || e?.status == null || e?.status >= 500
      if (!retryable || attempt >= 7) throw e
      await new Promise((r) => setTimeout(r, e?.message === 'too-fast' ? 2000 : 1500))
    }
  }
}
